import { useOktaAuth } from "@okta/okta-react";
import {
  GetDataWithCleanedDates,
  GetDataWithCleanedDatesOnSpecifiedColumns,
} from "shared/helpers/DateHelpers";
import axios from "axios";
import { GetCustomError } from "shared/helpers/AxiosHelpers";

import { useQuery } from "@tanstack/react-query";
import { useOktaHelpers } from "shared/hooks/useOktaHelpers";
import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";

export function useFetchClientGridData(
  apiGetEndpoint,
  apiParameters,
  onDataRetrievedProcessor, //provide this to provide custom logic after data is retrieved
  onFailureCallback,
  setClientGridParentData,
  dateFormattingColumns = [],
  // disableCache = false, // New parameter to control caching behavior
  useQueryOptions = null
) {
  const { oktaAuth } = useOktaAuth();
  const { invalidTokenOnErrorHandler } = useOktaHelpers();

  const dataKey = "useFetchClientGridDataDataKey";

  async function GetClientGridData() {
    let accessToken = oktaAuth.getAccessToken();

    var result = await axios
      .get(apiGetEndpoint, {
        params: apiParameters,
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((response) => {
        let dataToClean = null;

        //if a processor is provided, use it to process the data before calling the GetDataWithCleanedDates function
        if (onDataRetrievedProcessor) {
          dataToClean = onDataRetrievedProcessor(response.data);
        } else {
          dataToClean = response.data;
        }
        let cleanedData = [];
        if (dateFormattingColumns && dateFormattingColumns.length > 0) {
          cleanedData = GetDataWithCleanedDatesOnSpecifiedColumns(
            dataToClean,
            dateFormattingColumns
          );
        } else {
          cleanedData = GetDataWithCleanedDates(dataToClean);
        }

        if (setClientGridParentData) setClientGridParentData(cleanedData);

        return cleanedData;
      })
      .catch((err) => {
        //alert("GetClientGridData Error: " + JSON.stringify(err));
        // console.error(err);

        //will check if the token is valid and redirect to login page if not
        invalidTokenOnErrorHandler(err);

        if (onFailureCallback) onFailureCallback(err);

        let thisError = GetCustomError(err);

        throw thisError;
      });

    return result;
  }

  function IsEnabled() {
    if (IsNullOrEmpty(apiGetEndpoint)) return false;

    if (IsNullOrEmpty(apiParameters)) return false;

    //alert("IsEnabled: " + JSON.stringify(apiParameters));
    return true;
  }

  const queryResult = useQuery({
    queryKey: [dataKey, apiGetEndpoint, apiParameters],
    queryFn: async () => await GetClientGridData(),
    enabled: IsEnabled(),
    refetchOnWindowFocus:
      useQueryOptions !== null && useQueryOptions.refetchOnWindowFocus !== null
        ? useQueryOptions.refetchOnWindowFocus
        : true,
  });

  // When query is disabled (no valid parameters), return empty data instead of cached data
  // This prevents stale data from being displayed when parameters are cleared
  if (!IsEnabled()) {
    return {
      ...queryResult,
      data: undefined,
    };
  }

  return queryResult;
}
